import { api, type CapacityReport } from '../api';
import { barChart, bindCharts } from '../charts';
import { errorCard, esc, fmtDate, html, loading, pill, tile } from '../ui';

/**
 * Capacity.
 *
 * Not a scheduling engine, and not pretending to be one. It answers the two
 * questions a planner actually has: is anything booked that nobody is lined up
 * to do, and is anything sitting on parts. Both are silent failures otherwise —
 * a visit with no performing organisation looks identical to one that is fully
 * arranged until the week it lands.
 */
export function view(c: CapacityReport): string {
  const problems = c.orders.filter((o) => o.unassigned || o.awaitingParts);

  return html`
    <div class="page-h">
      <div>
        <h1>Capacity</h1>
        <p class="dim">
          Who can do the work, what is booked over the next ${c.weeks} weeks, and what is waiting on parts
        </p>
      </div>
      <a class="btn" href="/planning">Back to planning</a>
    </div>

    <div class="tiles">
      ${tile({ label: 'Visits booked', value: c.summary.visits, sub: `next ${c.weeks} weeks` })}
      ${tile({
        label: 'Nobody assigned',
        value: c.summary.unassigned,
        sub: c.summary.unassigned ? 'no performing organisation' : 'all assigned',
        tone: c.summary.unassigned ? 'exceed' : 'ok',
      })}
      ${tile({
        label: 'Waiting on parts',
        value: c.summary.awaitingParts,
        sub: 'cannot start',
        tone: c.summary.awaitingParts ? 'caution' : 'ok',
      })}
      ${tile({ label: 'Labour booked', value: c.summary.manHours, suffix: ' h', sub: 'estimated' })}
    </div>

    ${problems.length
      ? html`
          <div class="card">
            <div class="card-h">
              <div>
                <h2>Needs attention</h2>
                <p class="tiny dim">A visit with nobody assigned looks fully arranged until the week it lands</p>
              </div>
              ${pill('DUE_SOON', `${problems.length}`)}
            </div>
            <div class="rows">
              ${problems
                .map(
                  (o) => html`
                    <div class="row" style="border-left-color:var(--${o.unassigned ? 'exceed' : 'caution'})">
                      <div class="bd">
                        <div class="t">
                          <a href="/planning/${esc(o.id)}">${esc(o.reference)}</a> · ${esc(o.registration)} — ${esc(o.title)}
                        </div>
                        <div class="m">
                          ${o.unassigned ? 'No performing organisation assigned' : 'Awaiting parts'}
                          ${o.plannedStart ? ` · planned ${esc(fmtDate(o.plannedStart))}` : ' · no date set'}
                          ${o.parts.length ? ` · ${esc(o.parts.map((p) => p.partNumber).join(', '))}` : ''}
                        </div>
                      </div>
                      ${pill(o.unassigned ? 'OVERDUE' : 'DUE_SOON', o.unassigned ? 'Unassigned' : 'Parts')}
                    </div>
                  `,
                )
                .join('')}
            </div>
          </div>
        `
      : ''}

    <div class="card">
      <div class="card-h">
        <h2>Work booked by week</h2>
        <span class="lbl">estimated labour</span>
      </div>
      <div class="card-b">
        ${barChart(
          c.load.map((w) => ({ label: w.week.slice(5), value: w.manHours })),
          ' h',
          'Estimated labour hours booked per week',
        )}
      </div>
      <div class="card-f">
        ${c.load.length
          ? `${c.summary.visits} visit(s) across ${c.load.length} week(s), ${c.summary.manHours} estimated hours.`
          : 'Nothing booked with a date in this window.'}
      </div>
    </div>

    <div class="cols">
      <div class="card">
        <div class="card-h"><h2>Who can do the work</h2></div>
        <div class="rows">
          ${c.approvals.length
            ? c.approvals
                .map(
                  (a) => html`
                    <div class="row">
                      <div class="bd">
                        <div class="t">${esc(a.reference)} — ${esc(a.kind.replace(/_/g, '-'))}</div>
                        <div class="m">
                          ${esc(a.scopeNote ?? 'no scope recorded')}${a.expiresAt ? ` · expires ${esc(fmtDate(a.expiresAt))}` : ''}
                        </div>
                      </div>
                    </div>
                  `,
                )
                .join('')
            : '<div class="row"><div class="bd"><div class="m">No maintenance approval on file — nothing can be released.</div></div></div>'}
        </div>
      </div>

      <div class="card">
        <div class="card-h"><h2>Who can sign the release</h2></div>
        <div class="rows">
          ${c.certifyingStaff.length
            ? c.certifyingStaff
                .map(
                  (p) => html`
                    <div class="row">
                      <div class="bd">
                        <div class="t">${esc(p.displayName)}</div>
                        <div class="m">${esc(p.holds.join(' · '))}</div>
                      </div>
                    </div>
                  `,
                )
                .join('')
            : '<div class="row"><div class="bd"><div class="m">Nobody holds a CRS authorisation.</div></div></div>'}
        </div>
        <div class="card-f">
          Ground time is bookable; a signature is not. A visit with nobody able to release it is a visit
          that ends with the aircraft still unserviceable.
        </div>
      </div>
    </div>
  `;
}

export async function renderCapacity(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading capacity');
  try {
    const c = await api.reports.capacity();
    host.innerHTML = view(c);
    bindCharts(host);
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load capacity.');
  }
}
